import { ORDER_TYPES } from "../Enums/orderTypes";
import { arraysEqualAsSets } from "../Util/jsUtils";

export class CompanyReviewSummary {
  constructor(companyId, data) {
    this.companyId = companyId;
    this.orderAvg = data?.avg ?? 0;
    this.orderReviewsCount = data?.foodReviewsCount ?? 0;
    this.orderBreakdown = data?.breakdownArr ?? [0, 0, 0, 0, 0];
    this.driverAvg = data?.driverAvg ?? 0;
    this.driverReviewsCount = data?.driverReviewsCount ?? 0;
    this.driverBreakdown = data?.breakdownDriverArr ?? [0, 0, 0, 0, 0];
    this.driverSentiment = data?.sentiment?.delivery ?? {};
    this.orderSentiment = data?.sentiment?.food ?? {};
  }

  getCompanyId() {
    return this.companyId;
  }

  getOrderAvg() {
    return this.orderAvg.toFixed(2);
  }

  getOrderReviewsCount() {
    return this.orderReviewsCount;
  }

  getOrderBreakdown() {
    return this.orderBreakdown;
  }

  getDriverAvg() {
    return this.driverAvg.toFixed(2);
  }

  getDriverReviewsCount() {
    return this.driverReviewsCount;
  }

  getDriverBreakdown() {
    return this.driverBreakdown;
  }

  getDriverReviewSentimentCount() {
    return Object.values(this.driverSentiment).reduce(
      (acc, curr) => acc + curr.count,
      0
    );
  }

  getOrderReviewSentimentCount() {
    return Object.values(this.orderSentiment).reduce(
      (acc, curr) => acc + curr.count,
      0
    );
  }

  getDriverReviewPositiveSentiments() {
    return (
      this.driverSentiment?.positive?.keywords?.map(
        (keyword) => keyword.keyword
      ) ?? []
    );
  }

  getOrderReviewPositiveSentiments() {
    return (
      this.orderSentiment?.positive?.keywords?.map(
        (keyword) => keyword.keyword
      ) ?? []
    );
  }

  getDriverReviewNegativeSentiments() {
    return (
      this.driverSentiment?.negative?.keywords?.map(
        (keyword) => keyword.keyword
      ) ?? []
    );
  }

  getOrderReviewNegativeSentiments() {
    return (
      this.orderSentiment?.negative?.keywords?.map(
        (keyword) => keyword.keyword
      ) ?? []
    );
  }
}

export class AllCompanyReviewSummaryData {
  constructor(data, timeframe, startDate, endDate) {
    this.data = data.success ? data.summary : {};
    this.timeframe = timeframe;
    this.startDate = startDate;
    this.endDate = endDate;
  }

  getCompanyOrderRating(companyId) {
    return this.data[`${companyId}`]?.orderAvgRating ?? "N/A";
  }

  getCompanyDriverRating(companyId) {
    return this.data[companyId]?.driverAvgRating ?? "N/A";
  }
}

export class ReviewsList {
  constructor(data, timeframe) {
    this.reviews =
      data?.reviews?.map((review) => new SingleReview(review)) ?? [];
    this.timeframe = timeframe;
  }

  getAllReviews() {
    return this.reviews;
  }

  getTimeframe() {
    return this.timeframe;
  }
}

export class SingleReview {
  constructor(data) {
    this.carrierName = data?.carrierName ?? "";
    this.companyId = data?.companyId ?? -1;
    this.customerName = data?.customerName ?? "";
    this.orderNumber = data?.orderNumber ?? "";
    this.orderId = data?.orderId ?? -1;
    this.orderType = data?.orderType ?? "";
    this.orderRating = data?.rating ?? 0;
    this.driverRating = data?.driverRating ?? 0;
    this.reviewText = data?.review ?? "";
    this.reviewTime = data?.reviewTime ?? 0;
    this.reviewId = data?.reviewId ?? -1;
    this.customerPhoneNumber = data?.customerPhone ?? "";
    this.totalOrderCost = data?.totalOrderCost ?? 0;
    this.placementTime = data?.placementTime ?? "";
    this.deliveryTime = data?.deliveryTime ?? "";
  }

  getReviewTime() {
    return this.reviewTime;
  }

  getOrderNumber() {
    return this.orderNumber;
  }

  getOrderType() {
    return this.orderType;
  }

  getOrderRating() {
    return this.orderRating;
  }

  getDriverRating() {
    return this.orderType === ORDER_TYPES.DELIVERY ? this.driverRating : null;
  }

  getServiceRating() {
    return this.orderType === ORDER_TYPES.DINE_IN ? this.driverRating : null;
  }

  getReviewText() {
    return this.reviewText;
  }

  getReviewId() {
    return this.reviewId;
  }

  getCustomerName() {
    if (this.orderType === ORDER_TYPES.DINE_IN) {
      return this.customerName || "Anonymous";
    }
    return this.customerName;
  }

  getCustomerPhoneNumber() {
    return this.customerPhoneNumber;
  }

  getOrderId() {
    return this.orderId;
  }

  getCompanyId() {
    return this.companyId;
  }

  getCarrierName() {
    return this.carrierName;
  }

  getTotalOrderCost() {
    return this.totalOrderCost;
  }

  getPlacementTime() {
    return this.placementTime;
  }

  getDeliveryTime() {
    return this.deliveryTime;
  }
}

class SingleReviewChatRoom {
  constructor(data) {
    this.orderId = data?.orderId ?? -1;
    this.reviewId = data?.reviewId ?? -1;
    this.roomId = data?.roomId ?? -1;
    this.roomName = data?.roomName ?? "";
    this.customerReferenceId = data?.customerReferenceId ?? "";
    this.firstMessage = data?.firstMessage?.message ?? "";
    this.firstMessageSentAt = data?.firstMessage?.sendAt ?? 0;
  }
}

export class ReviewChatRooms {
  constructor(data, orderIds, reviewIds) {
    this.reviewRoomsByOrderId = {};
    this.reviewRoomsByReviewId = {};
    (data?.reviewRooms ?? []).forEach((reviewRoom) => {
      if (reviewRoom.orderId) {
        this.reviewRoomsByOrderId[reviewRoom.orderId] = new SingleReviewChatRoom(reviewRoom);
      }
      if (reviewRoom.reviewId) {
        this.reviewRoomsByReviewId[reviewRoom.reviewId] = new SingleReviewChatRoom(reviewRoom);
      }
    });
    this.orderIds = orderIds;
    this.reviewIds = reviewIds;
  }

  getReviewRoomByOrderId(orderId) {
    return this.reviewRoomsByOrderId[orderId];
  }

  getReviewRoomByReviewId(reviewId) {
    return this.reviewRoomsByReviewId[reviewId];
  }

  hasRoomKeysChanged({orderIds, reviewIds}) {
    return !arraysEqualAsSets(this.orderIds, orderIds) || !arraysEqualAsSets(this.reviewIds, reviewIds);
  }
}
